// Discover's hero (31 Jul 2026, Rob's ask): the front door's slideshow, in
// the app, over the reader's own new practices instead of a static manifest.
// The picture is the argument on the way in; the search and the five tabs are
// still right underneath it, unchanged.
//
// What it does differently from the front door's:
//
// - The whole picture opens the practice modal, which is the one place
//   Add / Later / No thanks lives, so the hero adds a way in and no new way
//   to decide. Nothing is dressed as a button or a link on the photograph
//   (31 Jul 2026, Rob): that shape reads as an advertisement. Outbound stays
//   off this surface (spec: outbound lives on the modal, feed, and profiles).
// - The expert is a face and a full name, not a byline in running text. In
//   the app the reader already follows these people, so the portrait is
//   recognition rather than attribution.
// - It holds still whenever it is not being watched: while the practice modal
//   is up over it, and once it has scrolled more than half off the top.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useStore } from "../state/store.js";
import type { CardView } from "../state/types.js";
import { Avatar } from "./Avatar.js";
import { rowTime } from "./fragments.js";
import { artFor } from "./placeholderArt.js";
import { HERO_SIZES, sentence, shuffled, useFitRow, useScrolledPast, useSlideshow, type Slide } from "./slideshow.js";
import { artImg } from "../assets.js";

interface DeckSlide extends Slide {
  cardId: string;
  chip: string | null;
  // What it asks of you, the same reading the rows use: dropped when it only
  // repeats the cadence.
  time: string;
  expertId: string;
}

// A dozen is a run of about ninety seconds, long enough that nobody watches
// the deck come round, and short enough that the browser is never holding
// more pictures than one screen will ever use. The pool it is drawn from is
// everything new, which for an established account is hundreds.
const DECK_SIZE = 12;
// Added to whatever the practice's length already earns it (31 Jul 2026, Rob).
const DISCOVER_BONUS_MS = 1000;

export function toSlide(card: CardView): DeckSlide {
  return {
    id: card.id,
    cardId: card.id,
    text: card.do,
    // A practice with no art of its own shows its topic's stand-in, so the
    // deck never mixes illustrated and bare slides.
    image: artFor(card),
    chip: card.chip,
    time: rowTime(card),
    expertId: card.expertId ?? "",
  };
}

// Reorder so the same expert never speaks twice running. Not one slide per
// expert: someone following four people would get a four-slide hero, and the
// pool is the whole of New (31 Jul 2026, after Rob spotted the repeats).
export function spaced<T extends { expertId: string }>(items: T[]): T[] {
  const rest = [...items];
  const out: T[] = [];
  while (rest.length) {
    const last = out[out.length - 1];
    const i = Math.max(0, rest.findIndex((x) => !last || x.expertId !== last.expertId));
    out.push(...rest.splice(i, 1));
  }
  return out;
}

// A fresh sample every visit, drawn from everything the reader has not
// decided on yet.
function draw(cards: CardView[], exclude: Set<string> = new Set(), room = DECK_SIZE): DeckSlide[] {
  const pool = cards.filter((c) => !exclude.has(c.id)).map(toSlide);
  return spaced(shuffled(pool).slice(0, room));
}

export function DiscoverHero({ cards, inset = false }: {
  cards: CardView[];
  // Inside a panel rather than at the top of a screen: shorter, and it keeps
  // the panel's own corner radius (31 Jul 2026, Today's Something new).
  inset?: boolean;
}) {
  const openSheet = useStore((s) => s.openSheet);
  const experts = useStore((s) => s.experts);
  // Any sheet, not only this card's: whatever is open is what the reader is
  // reading, and the pictures moving behind it are the distraction.
  const sheetOpen = useStore((s) => s.sheet !== null);
  const stage = useRef<HTMLDivElement>(null);
  const capRef = useRef<HTMLSpanElement>(null);
  const whoRef = useRef<HTMLSpanElement>(null);
  const doRef = useRef<HTMLButtonElement>(null);
  const scrolledPast = useScrolledPast(stage);
  // Drawn once at mount, then kept: a hero that reshuffled on every render
  // would never hold a slide still. What follows only trims and tops up.
  const [deck, setDeck] = useState<DeckSlide[]>(() => draw(cards));

  const ids = cards.map((c) => c.id).join("|");
  useEffect(() => {
    setDeck((prev) => {
      const live = new Set(cards.map((c) => c.id));
      const kept = prev.filter((s) => live.has(s.cardId));
      if (kept.length === prev.length && kept.length >= DECK_SIZE) return prev;
      // A decided practice leaves the deck, and another new one takes its
      // place, so a long session keeps finding fresh material rather than
      // grinding down to the last three.
      const top = draw(cards, new Set(kept.map((s) => s.cardId)), DECK_SIZE - kept.length);
      if (!top.length && kept.length === prev.length) return prev;
      return spaced([...kept, ...top]);
    });
  }, [ids, cards]);

  // A second longer on every slide than the length alone asks for (31 Jul
  // 2026, Rob), on the Discover page only: it is the whole screen there and
  // the reader is deciding something, where Today's inset panel is one card
  // among several and keeps the base pace.
  const { cur, prev, corner, next } = useSlideshow(deck, {
    paused: sheetOpen || scrolledPast,
    holdBonusMs: inset ? 0 : DISCOVER_BONUS_MS,
  });

  // Set as large as the frame allows, not as small as the longest practice
  // might need (31 Jul 2026, Rob). Length buckets were tried first and read
  // as three different designs; this keeps every slide at the CSS size and
  // steps a long one down a point at a time until the words, the byline and
  // the link all sit inside the picture. Layout effect, so the reader never
  // sees the first, too-large pass.
  const showing = cur?.id;
  useFitRow(whoRef, showing);
  useLayoutEffect(() => {
    const box = stage.current;
    const cap = capRef.current;
    const line = doRef.current;
    if (!inset || !box || !cap || !line) return;
    const fit = () => {
      line.style.fontSize = "";
      const pad = getComputedStyle(cap.parentElement as HTMLElement);
      const room = box.clientHeight - parseFloat(pad.paddingTop) - parseFloat(pad.paddingBottom);
      if (room <= 0) return;
      let size = parseFloat(getComputedStyle(line).fontSize);
      // Never below a readable size: past this the picture has to give, and
      // the clamp in the stylesheet catches the rest.
      while (cap.scrollHeight > room && size > 15) {
        size -= 1;
        line.style.fontSize = `${size}px`;
      }
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [showing, inset]);

  if (!cur) return null;
  const e = experts?.find((x) => x.id === cur.expertId);
  const open = () => openSheet({ kind: "practice", cardId: cur.cardId });
  // Which of the day's new things this is. Read off the deck at render, not
  // counted up as the slides turn: when the reader adds or passes on one it
  // leaves the deck, and a running tally would keep counting the practice
  // that is no longer there. One left is not a position, so it says nothing.
  const place = deck.findIndex((x) => x.id === cur.id) + 1;
  return (
    // The whole picture opens the practice modal, the same way the cards
    // below it do. The practice itself stays a real button, which is the
    // keyboard way in, exactly as on a suggestion card.
    <div className={`hslides apphero${inset ? " inset" : ""}`} ref={stage} onClick={open}>
      {prev && (
        <figure className="hslide out" key={`${prev.id}-out`} aria-hidden="true">
          <img className="hslide-img" alt="" {...artImg(prev.image)} sizes={HERO_SIZES} />
        </figure>
      )}
      {/* Inside a panel the words hold the top left corner and stay there
          (31 Jul 2026, Rob): the corner rotation is for a hero that is the
          top of a screen, and in a section it just makes the panel restless.
          Left, where the panel's own heading above it starts. */}
      <figure className="hslide" key={cur.id} data-pos={inset ? "tl" : corner}>
        <img className="hslide-img" alt="" {...artImg(cur.image)} sizes={HERO_SIZES} />
        {/* The marker is also the way forward (31 Jul 2026, Rob): pressing it
            turns to the next one rather than opening the modal, so a reader
            who wants to see the rest never has to wait out a hold. */}
        {inset && deck.length > 1 && place > 0 && (
          <button
            className="hslide-count"
            aria-label="Next new practice"
            onClick={(ev) => {
              ev.stopPropagation();
              next();
            }}
          >
            {place} of {deck.length}
          </button>
        )}
        <figcaption>
          <span className="hslide-cap" ref={capRef}>
            {e && (
              <span className="hslide-who" ref={whoRef}>
                <Avatar e={e} size="m" />
                {/* Name and metadata are one group so they can sit on one
                    line where there is room and stack into two where there is
                    not (31 Jul 2026, Rob): a long institution name left
                    nothing after it on a phone. The dots are drawn by CSS, so
                    the second line does not open with one. */}
                <span className="hslide-idn">
                  <span className="hslide-name" data-lead>
                    {e.name}
                  </span>
                  <span className="hslide-meta" data-trail>
                    {/* Plain dotted text, not a pill: the shape is for things
                        you press, and the four evidence colours are not
                        legible over a photograph (30 Jul 2026, front door). */}
                    {cur.chip && <span className="hslide-grade">{cur.chip}</span>}
                    {/* What it costs you, after the grade. */}
                    {cur.time && (
                      <span className="hslide-time" data-drop>
                        {cur.time}
                      </span>
                    )}
                  </span>
                </span>
              </span>
            )}
            <button
              className="hslide-do"
              ref={doRef}
              onClick={(ev) => {
                ev.stopPropagation();
                open();
              }}
            >
              {sentence(cur.text)}
            </button>
            {/* Today says "Review these to see if you want to add any", so
                the picture carries the same word (31 Jul 2026, Rob). It opens
                the practice modal, where the decision is made. */}
            {inset && (
              <span className="hslide-cta">
                <button
                  className="hslide-view"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    open();
                  }}
                >
                  Review ›
                </button>
              </span>
            )}
          </span>
        </figcaption>
      </figure>
    </div>
  );
}
